import {Component, TemplateRef, ViewChild} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {OverlayConfig, OverlayRef} from 'exc-controls';
import {Observable, Subject} from 'rxjs';
import {ExcFileItem} from '../models/ExcFileItem';
import {UploadFormStructureInterface} from '../interfaces/UploadFormStructure.interface';
import {DynamicInputsInterface} from '../interfaces/DynamicInputs.interface';
import {ExcTranslation} from 'exc-core';

/**
 * A component that contains a form to create or edit ExcFileItem instances.
 */
@Component({
  selector: 'lib-exc-upload-dialog',
  templateUrl: 'exc-upload-dialog.component.html'
})
export class ExcUploadDialogComponent extends ExcTranslation {

  /**
   * Template buttons for modal
   */
  @ViewChild('templateButtons') templateButtons: TemplateRef<any>;

  /**
   * Data model of the form
   */
  formData: UploadFormStructureInterface;
  formWasSubmittedOnce = false;
  seletedAdditional = null;
  @ViewChild('uploadForm') uploadForm: FormGroup;

  /**
   * If true, the component is in loading state. In loading state a spinner or a progress bar is shown.
   */
  isLoading = false;

  /**
   * A number between 0 and 100 that describes the progress of the upload in percent. The progress bar is only shown,
   * if the components isLoading flag is set to true.
   */
  progress: number;
  editMode: boolean;

  private _afterSubmit: Subject<ExcFileItem[]> = new Subject();
  /**
   * A observable that is fired after the form was submitted successfully
   */
  public afterSubmit: Observable<ExcFileItem[]> = this._afterSubmit.asObservable();

  /**
   * A list of names that present the dynamic input fields of the form upload component.
   */
  public dynamicInputs: DynamicInputsInterface[] = [];

  constructor(
    public overlayRef: OverlayRef,
    public overlayConfig: OverlayConfig
  ) {
    super();
    // set dynamic inputs if available
    if (overlayConfig.data && overlayConfig.data.dynamicInputs) {
      this.dynamicInputs = overlayConfig.data.dynamicInputs;
    }

    // handle data for editing (not completely supported yet)
    if (overlayConfig.data && overlayConfig.data.upload) {
      const fileItem = (overlayConfig.data.upload as ExcFileItem).getCopy();
      this.editMode = true;
      this.initFormData(fileItem);
    } else {
      this.editMode = false;
      this.initFormData();
    }
  }

  /**
   * Creates the initial formData object from dynamicInputs
   */
  private initFormData(fileData?: ExcFileItem) {
    this.formData = {files: []};

    this.dynamicInputs.forEach(item => {
      if (fileData) {
        this.formData[item.apiValue] = fileData.misc[item.apiValue];
      } else {
        this.formData[item.apiValue] = null;
      }
    });
  }

  /**
   * Closes the modal
   * @param data - data should be passed as observable data
   */
  close(data?: UploadFormStructureInterface) {
    this.overlayRef.close(data);
  }

  /**
   * Checks if the form is valid. If so, the afterSubmit observable is fired and the ExcFileItems that were
   * created from form data are passed.
   */
  save() {
    // usability improvements. Sets all form controls that were invalid and not filled from user in an error state
    Object.keys(this.uploadForm.controls).forEach(key => {
      this.uploadForm.controls[key].markAsDirty();
      this.uploadForm.controls[key].updateValueAndValidity();
    });
    this.formWasSubmittedOnce = true;

    // do not proceed if form is invalid
    if (this.uploadForm.invalid) {
      return;
    }

    // save form values for each ExcFileItem
    this.formData.files
      .forEach(item => {
        // do not consider files property of formData to avoid circular dependency
        item.misc = {...this.formData};
        item.AdditionalAudit = this.seletedAdditional;
        delete item.misc.files;
      });
    this._afterSubmit.next(this.formData.files);
  }

  /**
   * Sets the component in loading state
   */
  startLoadingAnimation() {
    this.isLoading = true;
  }

  /**
   * Ends the loading state of this component
   */
  stopLoadingAnimation() {
    this.isLoading = false;
  }
}
